import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { withTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import CargomaticSelect from 'components/v2/select';
import CustomerSearch from 'components/site/customer-search-alt';
import _ from 'lodash';
import { titleize, humanize } from 'underscore.string';
import {
  equipmentTypesMapping,
  assetTypes,
  equipmentTypes,
  equipmentSizes,
  CHASSIS_OWNERSHIP_TYPE,
} from 'constants/Equipment';
import { getCustomerName } from 'helpers/users';

const DISABLED_ORDER_STATUSES = ['created', 'en_route'];

const disableStatusDropdown = (equipment) => {
  const { order, _equipmentType } = equipment;

  if (_equipmentType && _equipmentType === 'container') {
    if (order) {
      const { order_type, status } = order;

      if (order_type === 'import' && DISABLED_ORDER_STATUSES.includes(status)) {
        return true;
      }
    }
  }

  return false;
};

const emptyTooltip = <Tooltip id="empty" bsClass="invisible" />;

const EXPORT_DATES = [
  'earliest_empty_date',
  'earliest_return_date',
  'last_cut_date',
];
const IMPORT_DATES = ['last_free_date'];
class EquipmentFormRow extends Component {
  static propTypes = {
    equipment: PropTypes.object.isRequired,
    actionsDisabled: PropTypes.bool.isRequired,
    onDeselect: PropTypes.func.isRequired,
    onApplyToAll: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
  };

  getValidationClassName(field) {
    const error = this.props.equipment.error || {};

    if (error[field]) {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  }

  disableStatusTooltip() {
    const { t } = this.props;
    return (
      <Tooltip id="equipment_status">{t('DISABLED_EQUIPMENT_TOOLTIP')}</Tooltip>
    );
  }

  disableDatePicker = (id) => {
    const { equipment } = this.props;

    if (equipment && equipment.order) {
      const {
        order: { order_type },
      } = equipment;
      if (order_type === 'import') {
        return !IMPORT_DATES.includes(id);
      }
      return !EXPORT_DATES.includes(id);
    }

    return true;
  };

  handleApplyToAll = () => this.props.onApplyToAll(this.props.equipment._id);

  handleChange = (field) => (e) => {
    const value = e.target.value;
    this.props.onFieldChange(this.props.equipment._id, field, value);
  };

  handleToggleChange = (field) => (value) => {
    this.props.onFieldChange(this.props.equipment._id, field, value);
  };

  handleDateChange = (field) => (date) =>
    this.props.onFieldChange(this.props.equipment._id, field, date);

  handleDeselect = () => this.props.onDeselect(this.props.equipment._id);

  renderEquipmentIdField() {
    const className = {
      ...this.getValidationClassName('equipment_id'),
    };

    const { equipment } = this.props;

    return (
      <div className={classnames(className)}>
        <input
          type="text"
          className="form-field w-full"
          value={equipment.equipment_id}
          onChange={this.handleChange('equipment_id')}
          placeholder="Enter Equipment Number"
        />
      </div>
    );
  }

  renderAssetTypeField() {
    const { equipment, t } = this.props;

    return (
      <div
        className={classnames(this.getValidationClassName('_equipmentType'))}
      >
        <select
          onChange={this.handleChange('_equipmentType')}
          value={equipment._equipmentType}
          className="form-select mb-4 w-full"
        >
          <option value="">{t('SELECT_ASSET_TYPE')}</option>
          {assetTypes.map((assetType) => (
            <option key={assetType} value={assetType}>
              {t(assetType.toUpperCase())}
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderStatuses() {
    const { equipment, t } = this.props;
    return (
      <div className={classnames(this.getValidationClassName('status'))}>
        <OverlayTrigger
          id={`${equipment._id}_status`}
          placement="top"
          overlay={
            disableStatusDropdown(equipment)
              ? this.disableStatusTooltip
              : emptyTooltip
          }
        >
          <span>
            <select
              onChange={this.handleChange('status')}
              value={equipment.status || ''}
              className="form-select mb-4 w-full"
              disabled={disableStatusDropdown(equipment)}
              style={{
                pointerEvents: disableStatusDropdown(equipment) ? 'none' : '',
              }}
            >
              <option value="">{t('SELECT_EQUIPMENT_STATUS')}</option>
              <option value="unavailable" key={0}>
                {t('UNAVAILABLE', { count: 1 })}
              </option>
              <option value="available" key={1}>
                {t('AVAILABLE', { count: 1 })}
              </option>
              <option value="closed_area" key={2}>
                {t('CLOSED_AREA')}
              </option>
              <option value="on_hold" key={3}>
                {t('ON_HOLD')}
              </option>
            </select>
          </span>
        </OverlayTrigger>
      </div>
    );
  }

  renderEquipmentType() {
    const { equipment, t } = this.props;
    const disableEquipmentType =
      equipment?._equipmentType !== equipmentTypesMapping.CONTAINER;
    return (
      <div className={classnames(this.getValidationClassName('_type'))}>
        <select
          onChange={this.handleChange('_type')}
          value={equipment._type}
          className="form-select mb-4 w-full"
          disabled={disableEquipmentType}
        >
          <option value="">{t('SELECT_EQUIPMENT_TYPE')}</option>
          {_.map(equipmentTypes, (label, key) => (
            <option key={key} value={key}>
              {t(sluggify(label))}
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderToggleSwitch() {
    const { equipment, t } = this.props;
    const disableIsPrivate =
      equipment?._equipmentType !== equipmentTypesMapping.CHASSIS;
    return (
      <div>
        <ToggleSwitch
          enabled={!!equipment?.is_private}
          disabled={disableIsPrivate}
          onChange={this.handleToggleChange('is_private')}
          data-testid="bulk-equipment-is-private-switch"
        />
      </div>
    );
  }

  renderChassisOwnerType() {
    const { equipment, t } = this.props;
    return (
      <CargomaticSelect
        selected={equipment?.chassis_owner_type}
        className="mx-1.5 w-full"
        disabled={!equipment?.is_private}
        onSelect={({ value }) => {
          this.props.onFieldChange(
            this.props.equipment._id,
            'chassis_owner_type',
            value,
          );
        }}
        hideLabel
        defaultSelectLabel=""
        items={CHASSIS_OWNERSHIP_TYPE}
        textKey="localizationSlug"
        valueKey="value"
        data-testid="bulk-equipment-owner-type-dropdown"
      />
    );
  }

  renderCustomerSearch() {
    const { equipment, t } = this.props;
    return (
      <CustomerSearch
        id="equipment-customer-search"
        clearOnSelect
        assignField
        disabled={equipment?.chassis_owner_type !== 'customer'}
        onChange={(_, chassis_owner) => {
          this.props.onFieldChange(
            this.props.equipment._id,
            'chassis_owner',
            chassis_owner,
          );
        }}
        onSelect={(_, chassis_owner) => {
          this.props.onFieldChange(
            this.props.equipment._id,
            'chassis_owner',
            chassis_owner,
          );
        }}
        value={getCustomerName(equipment?.chassis_owner)}
        placeholder={t('SEARCH_FOR_CUSTOMER')}
        type="customer"
        data-testid="bulk-equipment-customer-search"
      />
    );
  }
  renderEquipmentSizes() {
    const { equipment, t } = this.props;
    const disableEquipmentSize =
      equipment?._equipmentType !== equipmentTypesMapping.CONTAINER;
    return (
      <div
        className={classnames(this.getValidationClassName('_equipmentSize'))}
      >
        <select
          onChange={this.handleChange('_equipmentSize')}
          value={equipment._equipmentSize || ''}
          className="form-select mb-4 w-full"
          disabled={disableEquipmentSize}
        >
          <option value="">{t('SELECT_EQUIPMENT_SIZE')}</option>
          {equipmentSizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    );
  }

  renderColumns() {
    const { equipment } = this.props;

    const { _id: id } = equipment;

    const idKey = `id_${id}`;
    const assetTypeKey = `asset_type_${id}`;
    const statusKey = `status_${id}`;
    const typeKey = `type_${id}`;
    const sizeKey = `size_${id}`;
    const toggleKey = `toggle_${id}`;
    const chassisOwnerTypeKey = `chassis_owner_type_${id}`;
    const customerKey = `chassis_owner_${id}`;
    const applyAllKey = `apply_${id}`;
    const removeKey = `remove_${id}`;

    return [
      <td key={idKey} className="p-2">
        {this.renderEquipmentIdField()}
      </td>,
      <td key={assetTypeKey} className="p-2">
        {this.renderAssetTypeField()}
      </td>,
      <td key={statusKey} className="p-2">
        {this.renderStatuses()}
      </td>,
      <td key={typeKey} className="p-2">
        {this.renderEquipmentType()}
      </td>,
      <td key={sizeKey} className="p-2">
        {this.renderEquipmentSizes()}
      </td>,
      <td key={toggleKey} className="p-2">
        {this.renderToggleSwitch()}
      </td>,
      <td key={chassisOwnerTypeKey} className="p-2">
        {this.renderChassisOwnerType()}
      </td>,
      <td key={customerKey} className="p-2">
        {this.renderCustomerSearch()}
      </td>,
      <td key={applyAllKey} className="p-2">
        {this.renderApplyToAllButton()}
      </td>,
      <td key={removeKey} className="p-2">
        {this.renderRemoveActionColumn()}
      </td>,
    ];
  }

  renderApplyToAllButton() {
    const buttonClassName = {
      button: true,
      'button-hollow-green': true,
    };
    const { actionsDisabled, t } = this.props;
    const disabled = actionsDisabled;

    return (
      <button
        type="button"
        className={classnames(buttonClassName)}
        onClick={this.handleApplyToAll}
        disabled={disabled}
      >
        {titleize(t('APPLY'))}
      </button>
    );
  }

  renderRemoveActionColumn() {
    return (
      <button
        className="button button-secondary"
        onClick={this.handleDeselect}
        disabled={this.props.actionsDisabled}
      >
        <i className="fa fa-remove" />
      </button>
    );
  }

  render() {
    return <tr>{this.renderColumns()}</tr>;
  }
}

export default withTranslation()(EquipmentFormRow);
